import { ReactNode } from 'react';
import {
  FormattingContextProvider,
  useFormattingContext,
} from './useFormattingContext';
import {
  EntityFormattingRule,
  UnsupportedRuleWarning,
} from './FormattingRuleRow';
import { FlexHeader } from '../../atoms';
import { s32, s8, space } from '@ardoq/design-tokens';
import { DragAndDropSectionsProvider } from '@ardoq/drag-and-drop';
import { AddNewFormattingRuleButton } from './addNewFormattingGroupButton';
import { ConditionalFormattingProps } from '../../types';
import { FormattingRule } from './types';
import { FlexBox, Stack } from '@ardoq/layout';
import styled from 'styled-components';
import { header4 } from '@ardoq/typography';
import { GRID_TEMPLATE_COLUMNS } from './consts';
import { FieldFormattingVersion } from '@ardoq/api-types';
import { Checkbox } from '@ardoq/forms';
import { t, type WithI18nLocale } from '@ardoq/i18n';
import { AddNewFormattingGroupHelpText } from './AddNewFormattingGroupHelpText';
import { AiButton } from '@ardoq/button';

const GridHeader = styled.div`
  display: grid;
  grid-template-columns: ${GRID_TEMPLATE_COLUMNS};
  gap: ${s8};
  padding: 0 ${s8};
`;

const GridHeaderCell = styled.div`
  ${header4};
  padding-left: ${
    s8 // the header label should be horizontally aligned with the text in the editors below.
  };
  align-self: center;
`;

const CheckboxContainer = styled.div`
  margin-top: ${space.small};
  margin-bottom: ${space.small};
  width: fit-content;
`;

const FormattingRulesHeader = ({ i18nLocale }: WithI18nLocale) => (
  <FlexBox gap="small" paddingX="small">
    <FlexHeader style={{ width: 162, flex: '0 0 auto', paddingLeft: s32 }}>
      {t(i18nLocale, 'perspectives:conditionalFormatting.typeHeader', 'Type')}
    </FlexHeader>
    <FlexHeader>
      {t(
        i18nLocale,
        'perspectives:conditionalFormatting.attributeHeader',
        'Attribute'
      )}
    </FlexHeader>
    <FlexHeader>
      {t(
        i18nLocale,
        'perspectives:conditionalFormatting.conditionHeader',
        'Condition'
      )}
    </FlexHeader>
    <FlexHeader style={{ flex: '2' }}>
      {t(i18nLocale, 'perspectives:conditionalFormatting.valueHeader', 'Value')}
    </FlexHeader>
    <div style={{ width: 182 }} />
  </FlexBox>
);

const NewFormattingRulesHeader = ({ i18nLocale }: WithI18nLocale) => (
  <GridHeader>
    <GridHeaderCell />
    <GridHeaderCell />
    <GridHeaderCell>
      {t(i18nLocale, 'perspectives:conditionalFormatting.typeHeader', 'Type')}
    </GridHeaderCell>
    <GridHeaderCell>
      {t(i18nLocale, 'perspectives:conditionalFormatting.fieldHeader', 'Field')}
    </GridHeaderCell>
    <GridHeaderCell>
      {t(
        i18nLocale,
        'perspectives:conditionalFormatting.conditionHeader',
        'Condition'
      )}
    </GridHeaderCell>
    <GridHeaderCell>
      {t(i18nLocale, 'perspectives:conditionalFormatting.valueHeader', 'Value')}
    </GridHeaderCell>
    <GridHeaderCell />
  </GridHeader>
);
type PerspectivesConditionalFormattingUiProperties = {
  isSidebar?: boolean;
  children?: (rule: FormattingRule) => ReactNode;
  isNewConditionalFormatting: boolean;
  keepOriginalColor?: boolean;
  onKeepOriginalColorToggled?: (value: boolean) => void;
  isAiButtonVisible?: boolean;
  onAiButtonClicked?: VoidFunction;
};
const PerspectivesConditionalFormattingUi = ({
  isSidebar,
  children,
  isNewConditionalFormatting,
  keepOriginalColor = true,
  onKeepOriginalColorToggled,
  isAiButtonVisible,
  onAiButtonClicked,
}: PerspectivesConditionalFormattingUiProperties) => {
  const { rules, reorderRules, addRuleButtonOptions, i18nLocale } =
    useFormattingContext();
  const hasRules = !!Object.values(rules).length;

  const hasUnsupportedRules =
    isNewConditionalFormatting &&
    rules.some(
      rule => rule.version !== FieldFormattingVersion.NEW_CONDITIONAL_FORMATTING
    );

  return (
    <Stack gap="medium">
      {isNewConditionalFormatting && (
        <>
          <CheckboxContainer>
            <Checkbox
              isChecked={keepOriginalColor}
              onChange={(newValue: boolean) =>
                onKeepOriginalColorToggled?.(newValue)
              }
              isDisabled={!hasRules}
              popoverContent={
                !hasRules
                  ? t(
                      i18nLocale,
                      'perspectives:conditionalFormatting.noConditionalFormatPopover',
                      'No conditional format has been added yet so all data still keeps its original color'
                    )
                  : undefined
              }
            >
              {t(
                i18nLocale,
                'perspectives:conditionalFormatting.keepOriginalColor',
                'Keep original color of non-formatted data'
              )}
            </Checkbox>
          </CheckboxContainer>
          <Stack>
            <FlexBox gap="small" flexWrap>
              <AddNewFormattingRuleButton
                buttonOptions={addRuleButtonOptions}
              />
              {isSidebar && isAiButtonVisible && onAiButtonClicked && (
                <AiButton i18nLocale={i18nLocale} onClick={onAiButtonClicked}>
                  {t(
                    i18nLocale,
                    'perspectives:aiButton.conditionalFormatting.text',
                    'Format with AI'
                  )}
                </AiButton>
              )}
            </FlexBox>
            <AddNewFormattingGroupHelpText />
          </Stack>
        </>
      )}
      {hasUnsupportedRules && (
        <UnsupportedRuleWarning
          isMultiline={!isSidebar}
          i18nLocale={i18nLocale}
        />
      )}

      {hasRules &&
        !isSidebar &&
        (isNewConditionalFormatting ? (
          <NewFormattingRulesHeader i18nLocale={i18nLocale} />
        ) : (
          <FormattingRulesHeader i18nLocale={i18nLocale} />
        ))}
      {/* @ts-expect-error todo */}
      <DragAndDropSectionsProvider onDrop={reorderRules}>
        {rules.map(rule =>
          children ? (
            children(rule)
          ) : (
            <EntityFormattingRule
              key={rule.__id}
              dragAndDropCardId={rule.__id}
              formattingRule={rule}
              isNewConditionalFormatting={isNewConditionalFormatting}
            />
          )
        )}
      </DragAndDropSectionsProvider>
      {!isNewConditionalFormatting && (
        <Stack>
          <AddNewFormattingRuleButton buttonOptions={addRuleButtonOptions} />
          <AddNewFormattingGroupHelpText />
        </Stack>
      )}
    </Stack>
  );
};
type PerspectivesConditionalFormattingProperties =
  ConditionalFormattingProps & {
    isSidebar?: boolean;
    children?: (rule: FormattingRule) => ReactNode;
    isNewConditionalFormatting: boolean;
    keepOriginalColor?: boolean;
    onKeepOriginalColorToggled?: (value: boolean) => void;
  };
export const PerspectivesConditionalFormatting = ({
  formattingRules,
  onFormattingRuleUpdated,
  onFormattingRuleDeleted,
  onFormattingRuleReordered,
  tagsFormattingGroups,
  isSidebar,
  children,
  isNewConditionalFormatting,
  keepOriginalColor,
  onKeepOriginalColorToggled,
  isAiButtonVisible,
  onAiButtonClicked,
  ...rest
}: PerspectivesConditionalFormattingProperties) => {
  return (
    <FormattingContextProvider
      contextState={{
        rules: formattingRules,
        removeRule: onFormattingRuleDeleted,
        updateRule: onFormattingRuleUpdated,
        reorderRules: onFormattingRuleReordered,
        tagsFormattingGroups,
        ...rest,
      }}
    >
      <PerspectivesConditionalFormattingUi
        isSidebar={isSidebar}
        isNewConditionalFormatting={isNewConditionalFormatting}
        keepOriginalColor={keepOriginalColor}
        onKeepOriginalColorToggled={onKeepOriginalColorToggled}
        isAiButtonVisible={isAiButtonVisible}
        onAiButtonClicked={onAiButtonClicked}
      >
        {children}
      </PerspectivesConditionalFormattingUi>
    </FormattingContextProvider>
  );
};
